/**
 * usePreferences — manages user preferences in localStorage.
 * Key: "lunch-finder-preferences"
 * Stores: radiusKm, lastProfileId, selectedCategories
 */

import { useState, useCallback, useEffect } from 'react';
import { Preferences, Category } from '../types';

const STORAGE_KEY = 'lunch-finder-preferences';

const DEFAULT_PREFERENCES: Preferences = {
  radiusKm: 10,
  lastProfileId: null,
  selectedCategories: [],
};

function loadPreferences(): Preferences {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_PREFERENCES;
    const parsed = JSON.parse(raw) as Partial<Preferences>;
    return {
      radiusKm: parsed.radiusKm ?? DEFAULT_PREFERENCES.radiusKm,
      lastProfileId: parsed.lastProfileId ?? DEFAULT_PREFERENCES.lastProfileId,
      selectedCategories: parsed.selectedCategories ?? DEFAULT_PREFERENCES.selectedCategories,
    };
  } catch {
    return DEFAULT_PREFERENCES;
  }
}

function savePreferences(prefs: Preferences): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // Ignore storage errors (e.g., private mode)
  }
}

export function usePreferences() {
  const [preferences, setPreferences] = useState<Preferences>(loadPreferences);

  // Sync to localStorage on change
  useEffect(() => {
    savePreferences(preferences);
  }, [preferences]);

  const updatePreferences = useCallback((update: Partial<Preferences>) => {
    setPreferences((prev) => ({ ...prev, ...update }));
  }, []);

  const setRadius = useCallback((radiusKm: Preferences['radiusKm']) => {
    setPreferences((prev) => ({ ...prev, radiusKm }));
  }, []);

  const setCategories = useCallback((selectedCategories: Category[]) => {
    setPreferences((prev) => ({ ...prev, selectedCategories }));
  }, []);

  const setLastProfile = useCallback((lastProfileId: string | null) => {
    setPreferences((prev) => ({ ...prev, lastProfileId }));
  }, []);

  return {
    preferences,
    updatePreferences,
    setRadius,
    setCategories,
    setLastProfile,
  };
}
